import * as Yup from 'yup';
import type { InternalColumnDef, ValidationDef } from '../schema/types';
import { evaluateCondition } from '../../utils/evaluateCondition';
import { applyBuiltInTest } from './builtInTests';
import {
  coalesceValidationRow,
  type ValidationCoalesceRule,
} from './coerce';

function baseSchemaForType(type: ValidationDef['type']): Yup.AnySchema {
  switch (type) {
    case 'string':
      return Yup.string().nullable();
    case 'number':
      return Yup.number().nullable().transform((value, original) =>
        original === '' || original === null || original === undefined ? undefined : value,
      );
    case 'date':
      return Yup.date().nullable();
    case 'boolean':
      return Yup.boolean().nullable();
    case 'array':
      return Yup.array().nullable();
    case 'mixed':
    default:
      return Yup.mixed().nullable();
  }
}

function applyRules(
  schema: Yup.AnySchema,
  rules: ValidationDef['rules'] | undefined,
): Yup.AnySchema {
  if (!rules?.length) return schema;
  let current = schema;
  for (const rule of rules) {
    const method = rule.method;
    const args = rule.args ?? [];
    const message = rule.message ?? 'Invalid value';
    const yupMethod = (current as unknown as Record<string, unknown>)[method];
    if (typeof yupMethod === 'function') {
      current = (yupMethod as (...a: unknown[]) => Yup.AnySchema).call(current, ...args, message);
    }
  }
  return current;
}

function buildFieldSchema(validation: ValidationDef): Yup.AnySchema {
  let schema = baseSchemaForType(validation.type);

  if (validation.tests?.length) {
    for (const test of validation.tests) {
      schema = applyBuiltInTest(schema, test.name, test.params, test.message ?? 'Invalid value');
    }
  }

  schema = applyRules(schema, validation.rules);

  if (validation.when?.length) {
    for (const clause of validation.when) {
      const deps = Array.isArray(clause.dependsOn) ? clause.dependsOn : [clause.dependsOn];
      const mode = clause.mode ?? (deps.length > 1 ? 'all' : 'any');

      schema = schema.when(deps, {
        is: (...values: unknown[]) => {
          if (clause.is !== undefined) {
            return mode === 'all'
              ? values.every((v) => v === clause.is)
              : values.some((v) => v === clause.is);
          }
          return mode === 'all' ? values.every((v) => Boolean(v)) : values.some((v) => Boolean(v));
        },
        then: (inner) => applyRules(inner, clause.then),
        otherwise: (inner) => applyRules(inner, clause.otherwise),
      });
    }
  }

  return schema;
}

export function hasSchemaValidation(columns: ReadonlyArray<InternalColumnDef>): boolean {
  return columns.some((column) => column.editable?.validation != null);
}

export function validateRowFromSchema(
  columns: ReadonlyArray<InternalColumnDef>,
  row: Record<string, unknown>,
  coalesceRules?: ReadonlyArray<ValidationCoalesceRule> | null,
): Record<string, string> {
  const preparedRow = coalesceValidationRow(row, coalesceRules);
  const shape: Record<string, Yup.AnySchema> = {};
  let skippedWhenCount = 0;

  for (const column of columns) {
    const editable = column.editable;
    if (!editable?.validation) continue;
    if (editable.when && !evaluateCondition(editable.when, preparedRow)) {
      skippedWhenCount += 1;
      continue;
    }
    shape[column.field] = buildFieldSchema(editable.validation);
  }

  const errors: Record<string, string> = {};

  if (Object.keys(shape).length === 0) {
    return {};
  }

  const schema = Yup.object().shape(shape);

  try {
    schema.validateSync(preparedRow, { abortEarly: false });
  } catch (err: unknown) {
    const yupErr = err as { inner?: Array<{ path?: string; message: string }> };
    yupErr.inner?.forEach((error) => {
      if (error.path && !errors[error.path]) {
        errors[error.path] = error.message;
      }
    });
  }

  return errors;
}
